import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
// 🔑 공용 가드는 뒤로가기 전용 입구(`/back`)로 가져온다 — 패키지 입구(index)는 이 앱에 없는 의존까지 끌어온다.
import { isFirstInAppEntry, useExitGuard as useSharedExitGuard } from "@teachermate/shared/back";

/*
 * 스튜디오 나가기 확인 — 화면(대화상자·문구·포커스)은 이 앱의 것, 뒤로가기 처리는 **공용 부품 하나**다.
 * 🔙 2026-09-28 §9.H-18 v2.4 「한 칸 뒤로」 — 옛 판은 이 파일이 직접 popstate 를 가로채(자작 가드) 어느 단계에서든
 *    뒤로가기 = 나가기 확인이었다. 이제 단계 사이 뒤로가기는 바로 전 단계(주소마다 단계 · 자체 라우터가 따라간다),
 *    나가기 확인은 **첫 화면에서만**(이 앱 안에서 쌓인 이전 화면이 없는 곳 — 입장 직후 · QR·새로고침 직행).
 *    새로고침 경고는 못 보낸 입력이 있을 때만(dirty).
 */

export interface ExitGuardOptions {
  when: boolean;
  dirty: boolean;
  onExit: () => void;
  message?: string;
}

export interface ExitGuardController {
  requestExit: () => void;
  isOpen: boolean;
  message: string;
  cancelExit: () => void;
  confirmExit: () => void;
  dialogRef: RefObject<HTMLDivElement | null>;
}

const focusableSelector = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

export function useExitGuard(options: ExitGuardOptions): ExitGuardController {
  const optionsRef = useRef(options);
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const isOpenRef = useRef(false);
  const [message, setMessage] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  optionsRef.current = options;
  const shared = useSharedExitGuard({
    when: options.when && isFirstInAppEntry(),
    onConfirmExit: () => optionsRef.current.onExit(),
    unloadWhen: options.dirty,
  });
  const sharedRef = useRef(shared);
  sharedRef.current = shared;

  const requestExit = useCallback(() => {
    const current = optionsRef.current;
    if (!current.when) {
      current.onExit();
      return;
    }
    if (isOpenRef.current) return;

    const activeElement = document.activeElement;
    triggerRef.current = activeElement instanceof HTMLElement ? activeElement : null;
    setMessage(current.message ?? (
      current.dirty
        ? "작성한 내용은 이 기기에 24시간 보관돼요. 나중에 같은 기기에서 이어서 만들 수 있어요."
        : "작성한 내용과 완성된 노래는 그대로 남아요."
    ));
    isOpenRef.current = true;
    setIsOpen(true);
  }, []);

  const cancelExit = useCallback(() => {
    isOpenRef.current = false;
    setIsOpen(false);
    if (sharedRef.current.promptOpen) sharedRef.current.cancelExit();
    const trigger = triggerRef.current;
    window.requestAnimationFrame(() => {
      if (trigger?.isConnected) trigger.focus();
    });
  }, []);

  const confirmExit = useCallback(() => {
    isOpenRef.current = false;
    setIsOpen(false);
    // 뒤로가기가 연 확인이면 공용 가드가 보호 칸을 치운 뒤 나간다. 버튼이 연 확인이면 칸을 치우고(소유 시) 나간다.
    if (sharedRef.current.promptOpen) sharedRef.current.confirmExit();
    else sharedRef.current.releaseAndNavigate(() => optionsRef.current.onExit());
  }, []);

  // 뒤로가기(첫 화면)가 연 확인창도 같은 대화상자·같은 문구로 보인다.
  useEffect(() => {
    if (!shared.promptOpen || isOpenRef.current) return;
    const current = optionsRef.current;
    setMessage(current.message ?? (
      current.dirty
        ? "작성한 내용은 이 기기에 24시간 보관돼요. 나중에 같은 기기에서 이어서 만들 수 있어요."
        : "작성한 내용과 완성된 노래는 그대로 남아요."
    ));
    triggerRef.current = null;
    isOpenRef.current = true;
    setIsOpen(true);
  }, [shared.promptOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    dialog.querySelector<HTMLElement>("[data-exit-dialog-cancel]")?.focus();
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        cancelExit();
        return;
      }
      if (event.key !== "Tab") return;

      const focusableElements = Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector));
      if (focusableElements.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusableElements[0];
      const last = focusableElements.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [cancelExit, isOpen]);

  return {
    requestExit,
    isOpen,
    message,
    cancelExit,
    confirmExit,
    dialogRef,
  };
}
